<template>
  <HrWorkforcePanel title="Branch summary" :pager="pager.page_count > 1 ? pager : null" @update:page="emit('update:page', $event)">
    <template #actions>
      <input :value="search" type="text" class="form-control form-control-sm hw-search" placeholder="Search branch..." @input="onSearch">
    </template>
    <div class="table-responsive">
      <table class="table mb-0 hw-table">
        <thead>
          <tr>
            <th rowspan="2" class="hw-sortable hw-sticky-col" @click="toggleSort('name')">Branch <span class="hw-sort">{{ sortIcon('name') }}</span></th>
            <th rowspan="2" class="hw-sortable hw-num-cell" @click="toggleSort('total')">Total <span class="hw-sort">{{ sortIcon('total') }}</span></th>
            <th
              v-for="group in tableGroups"
              :key="group.group"
              :colspan="group.columns.length"
              class="hw-group-head hw-group-start"
            >{{ group.title }}</th>
          </tr>
          <tr>
            <th
              v-for="column in tableColumns"
              :key="column.id"
              class="hw-sortable hw-num-cell"
              :class="{ 'hw-group-start': column.is_first }"
              :title="column.title"
              @click="toggleSort(column.id)"
            >{{ column.label }} <span class="hw-sort">{{ sortIcon(column.id) }}</span></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in pagedRows"
            :key="row.key"
            :class="{ 'hw-row-active': selectedBranch === row.key }"
            @click="emit('select-branch', row.key)"
          >
            <td class="hw-branch-cell hw-sticky-col">{{ row.name }}</td>
            <td class="hw-num-cell hw-total-cell">{{ onlyCommaFormat(row.summary.total) }}</td>
            <td
              v-for="column in tableColumns"
              :key="column.id"
              class="hw-num-cell"
              :class="{ 'hw-group-start': column.is_first, 'hw-zero': cellValue(row.summary, column) === 0 }"
            >{{ formatCell(cellValue(row.summary, column)) }}</td>
          </tr>
          <tr v-if="tableRows.length === 0">
            <td :colspan="tableColumns.length + 2" class="text-center hw-muted">No branch found.</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td class="hw-sticky-col">Total</td>
            <td class="hw-num-cell">{{ onlyCommaFormat(summary.all.total) }}</td>
            <td
              v-for="column in tableColumns"
              :key="column.id"
              class="hw-num-cell"
              :class="{ 'hw-group-start': column.is_first }"
            >{{ formatCell(cellValue(summary.all, column)) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </HrWorkforcePanel>
</template>

<script setup>
import { computed, toRef } from 'vue'
import figureFormatter from '@/services/utils/figureFormatter'
import { notSpecifiedKey, profileStatusCounts, profileStatusLabels, useHrWorkforcePagedRows } from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/hrWorkforce'
import HrWorkforcePanel from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforcePanel.vue'

const props = defineProps({
  summary: { type: Object, required: true },
  labels: { type: Object, required: true },
  branchRows: { type: Array, required: true },
  selectedBranch: { type: String, required: true },
  search: { type: String, default: '' },
  sortKey: { type: String, default: 'total' },
  sortAsc: { type: Boolean, default: false },
  page: { type: Number, default: 1 }
})

const emit = defineEmits(['select-branch', 'update:search', 'update:sortKey', 'update:sortAsc', 'update:page'])

const pageSize = 15

const { onlyCommaFormat } = figureFormatter()

const columnGroups = computed(() => [
  { group: 'category', title: 'Employment type', labelMap: props.labels.categories },
  { group: 'gender', title: 'Gender', labelMap: props.labels.genders },
  { group: 'religion', title: 'Religion', labelMap: props.labels.religions },
  { group: 'marital_status', title: 'Marital status', labelMap: props.labels.marital_statuses },
  { group: 'age', title: 'Age', labelMap: props.labels.age_ranges },
  { group: 'department', title: 'Department', labelMap: props.labels.departments },
  { group: 'designation', title: 'Designation', labelMap: props.labels.designations },
  { group: 'division', title: 'District Fellowship', labelMap: props.labels.divisions },
  { group: 'job_status', title: 'Job status', labelMap: props.labels.job_statuses },
  { group: 'tenure', title: 'Tenure', labelMap: props.labels.tenures },
  { group: 'profile_status', title: 'Profile completion', labelMap: profileStatusLabels }
])

const fieldCount = computed(() => Object.keys(props.labels.profile_fields || {}).length)

const tableGroups = computed(() => columnGroups.value.map(spec => ({
  ...spec,
  columns: [...Object.keys(spec.labelMap || {}), notSpecifiedKey]
    .map(key => ({
      id: spec.group + ':' + key,
      group: spec.group,
      key,
      label: key === notSpecifiedKey ? 'N/S' : spec.labelMap[key],
      title: key === notSpecifiedKey ? 'Not specified' : spec.labelMap[key]
    }))
    .filter(column => cellValue(props.summary.all, column) > 0)
    .map((column, index) => ({ ...column, is_first: index === 0 }))
})).filter(group => group.columns.some(column => column.key !== notSpecifiedKey)))

const tableColumns = computed(() => tableGroups.value.flatMap(group => group.columns))

const tableRows = computed(() => {
  const search = props.search.trim().toLowerCase()
  const direction = props.sortAsc ? 1 : -1
  return props.branchRows
    .filter(row => row.name.toLowerCase().includes(search))
    .sort((a, b) => compareValues(sortValue(a), sortValue(b)) * direction)
})

const { pagedRows, pager } = useHrWorkforcePagedRows(tableRows, toRef(props, 'page'), pageSize)

const sortArrow = computed(() => props.sortAsc ? '▲' : '▼')

const formatCell = (value) => value > 0 ? onlyCommaFormat(value) : '–'

const cellValue = (rowSummary, column) => {
  const counts = column.group === 'profile_status' ? profileStatusCounts(rowSummary.profile, fieldCount.value) : rowSummary[column.group]
  if (!counts) return 0
  return counts[column.key] || 0
}

const sortValue = (row) => {
  if (props.sortKey === 'name') return row.name.toLowerCase()
  if (props.sortKey === 'total') return row.summary.total
  const column = tableColumns.value.find(item => item.id === props.sortKey)
  if (!column) return 0
  return cellValue(row.summary, column)
}

const compareValues = (a, b) => typeof a === 'string' ? a.localeCompare(b) : a - b

const toggleSort = (key) => {
  emit('update:sortAsc', props.sortKey === key ? !props.sortAsc : key === 'name')
  emit('update:sortKey', key)
}

const sortIcon = (key) => props.sortKey === key ? sortArrow.value : ''

const onSearch = (event) => {
  emit('update:search', event.target.value)
  emit('update:page', 1)
}
</script>

<style scoped src="./common/hrWorkforce.css"></style>

<style scoped>
.hw-search {
  width: 260px;
  max-width: 100%;
  border-radius: 8px;
}
.hw-table {
  font-size: 0.85rem;
}
.hw-table th,
.hw-table td {
  padding: 9px 12px !important;
  vertical-align: middle;
  white-space: nowrap;
}
.hw-table thead th {
  background: #f8fafc;
  color: #64748b;
  font-size: 0.75rem !important;
  font-weight: 600;
  text-transform: none !important;
  letter-spacing: 0 !important;
  border-bottom: 1px solid #e2e8f0;
}
.hw-group-head {
  text-align: center;
  color: #334155 !important;
  font-weight: 700 !important;
  border-bottom: 1px solid #e2e8f0;
}
.hw-num-cell {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.hw-sortable {
  cursor: pointer;
  user-select: none;
}
.hw-sort {
  font-size: 0.65rem;
  color: #0f766e;
}
.hw-group-start {
  border-left: 1px solid #e2e8f0;
}
.hw-table tbody td {
  color: #334155;
  border-bottom: 1px solid #f1f5f9;
}
.hw-table tbody tr {
  cursor: pointer;
}
.hw-table tbody tr:hover td {
  background: #f8fafc;
}
.hw-branch-cell {
  font-weight: 600;
  color: #1e293b !important;
}
.hw-total-cell {
  font-weight: 700;
  color: #0f172a !important;
}
.hw-zero {
  color: #cbd5e1 !important;
}
.hw-row-active td {
  background: #f0fdfa !important;
}
.hw-table tfoot td {
  font-weight: 700;
  color: #0f172a;
  background: #f8fafc;
  border-top: 2px solid #e2e8f0;
}
.hw-sticky-col {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
  box-shadow: inset -1px 0 0 #e2e8f0;
}
.hw-table thead .hw-sticky-col {
  z-index: 2;
  background: #f8fafc;
}
</style>
